import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {FmlxTooltip} from 'fmlx-common-ui';

import {checkContainIllegalChars} from '../../../../lib/stringHelper';

import './CardName.scss';

function CardName({name, onCardNameChange, className, invalid, runtimeMode, maxCharacters, isReadOnly}) {
  const [nameEditMode, setNameEditMode] = useState(false);
  const [cardName, setCardName] = useState(name);
  const [inputWidth, setInputWidth] = useState();
  const [maxWidth, setMaxWidth] = useState();

  const helperRef = useRef();

  useEffect(() => {
    setCardName(name);
  }, [name]);

  useEffect(() => {
    if (invalid) {
      setMaxWidth(160);
    } else {
      setMaxWidth(185);
    }
  }, [invalid]);

  useEffect(() => {
    if (helperRef.current) {
      const helperWidth = helperRef.current.offsetWidth;
      setInputWidth(helperWidth + 4);
    }
  }, [cardName]);

  const onChangeHandler = (e) => {
    const {value} = e.target;

    if (checkContainIllegalChars(value)) {
      return;
    }

    if (value.length > maxCharacters) {
      return;
    }
    setCardName(value);
  };

  const onBlurHandler = () => {
    setNameEditMode(false);

    if (checkContainIllegalChars(cardName.trim())) {
      return null;
    }

    if (cardName.trim().length === 0) {
      return setCardName(name);
    }

    return onCardNameChange(cardName);
  };

  const onKeyDownHandler = (e) => {
    if (e.keyCode === 13) {
      onBlurHandler();
    }
  };

  const onSetNameEditMode = () => {
    if (!isReadOnly) {
      setNameEditMode(!runtimeMode);
    }
  };

  return (
    <div className="card-component-name-wrapper">
      {!nameEditMode ? (
        <FmlxTooltip title={inputWidth > maxWidth ? cardName : ''}>
          <span
            className={`card-component-name${isReadOnly ? ' read-only' : ''}`}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={onSetNameEditMode}
            role="presentation"
          >
            {cardName}
          </span>
        </FmlxTooltip>
      ) : (
        <input
          className={`card-component-name-input ${className}`}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          type="text"
          value={cardName}
          onFocus={(e) => e.target.select()}
          onChange={(e) => onChangeHandler(e)}
          onBlur={onBlurHandler}
          onKeyDown={(e) => onKeyDownHandler(e)}
          style={{width: `${inputWidth}px`, maxWidth: `${maxWidth}px`}}
        />
      )}
      <span ref={helperRef} className="card-component-input-helper">
        {cardName}
      </span>
    </div>
  );
}

CardName.propTypes = {
  onCardNameChange: PropTypes.func.isRequired,

  name: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,

  invalid: PropTypes.bool.isRequired,
  runtimeMode: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  maxCharacters: PropTypes.number,
};

CardName.defaultProps = {
  maxCharacters: 25,
};

const areEqual = (prevProps, nextProps) =>
  prevProps.name === nextProps.name &&
  prevProps.className === nextProps.className &&
  prevProps.invalid === nextProps.invalid &&
  prevProps.runtimeMode === nextProps.runtimeMode &&
  prevProps.maxCharacters === nextProps.maxCharacters;

export default React.memo(CardName, areEqual);
